'use client';

import { useEffect, useState } from 'react';
import { tv, type VariantProps } from 'tailwind-variants';
import * as Dialog from '@/app_components/ui/Dialog';
import { usePaywall } from '@/app_lib/providers/PaywallProvider';
import { useStickyFooter } from '@/app_lib/providers/StickyFooterProvider';
import { cn } from '@/app_lib/utils/utils';

const modalStyles = tv({
  base: 'h-fit w-fit z-modal-content transition-transform outline-none focus:outline-none',
  variants: {
    position: {
      center:
        'fixed top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%] data-[state=open]:fade-in data-[state=closed]:fade-out max-md:w-full',
      left: 'fixed top-0 left-0 data-[state=closed]:slide-to-left data-[state=open]:slide-from-left',
      right: 'fixed top-0 right-0 data-[state=open]:slide-from-right data-[state=closed]:slide-to-right',
    },
  },
});

type ModalVariants = VariantProps<typeof modalStyles>;

interface BasicModalProps extends ModalVariants {
  className?: string;
  trigger: React.ReactNode;
  content: React.ReactNode;
  title?: string;
  description?: string;
  ndrModal?: boolean;
}

export const BasicModal = ({
  content,
  trigger,
  position = 'center',
  title = 'Modal',
  description = 'This is an opened modal',
  ndrModal = false,
}: BasicModalProps) => {
  const [open, setOpen] = useState(false);

  const { showPaywallax } = usePaywall();
  const { renderStickyFooter } = useStickyFooter();

  useEffect(() => {
    if (ndrModal) {
      const timer = setTimeout(() => {
        setOpen(true);
      }, 15000);

      return () => clearTimeout(timer);
    }
  }, [ndrModal]);

  return (
    <Dialog.Root
      onOpenChange={() => {
        if (window.s2nApi) {
          if (!showPaywallax && renderStickyFooter) {
            window.s2nApi.float = false;
          } else {
            window.s2nApi.float = open;
          }
        }
        setOpen((prev) => !prev);
      }}
      open={open}
    >
      <Dialog.Trigger
        asChild
        tabIndex={0}
      >
        {trigger}
      </Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay
          className={cn(
            'fixed inset-0 bg-black/50 transition-opacity z-modal-backdrop',
            'data-[state=open]:fade-in data-[state=closed]:fade-out',
          )}
        />
        <Dialog.Content
          asChild
          className={cn(modalStyles({ position }))}
        >
          <div>
            <Dialog.Title className='sr-only'>{title}</Dialog.Title>
            <Dialog.Description className='sr-only'>{description}</Dialog.Description>
            {content}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
